/**
 * AdminShell — Persistent left-sidebar navigation for every door's admin pages.
 *
 *   <AdminShell door="university" title="Alumni Directory">
 *     ... page content ...
 *   </AdminShell>
 *
 * Active section is auto-detected from `useLocation()`.
 * Mobile collapses to a hamburger-triggered drawer.
 */
import React, { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import {
  GraduationCap, BookOpen, Users, FileText, Calendar, Stethoscope, Award,
  DollarSign, MessageSquare, Library, ArrowLeft, Menu, X, Heart, Building,
  ClipboardList, Pill, Compass, Sparkles, ChevronRight,
} from "lucide-react";

const NAV = {
  university: {
    label: "University",
    icon: GraduationCap,
    items: [
      { label: "Dashboard / Admissions", to: "/admin/university", icon: Users },
      { label: "Catalog", to: "/admin/university/catalog", icon: Library },
      { label: "Enrollments", to: "/admin/university/enrollments", icon: FileText },
      { label: "LMS / Courses", to: "/admin/university/lms", icon: BookOpen },
      { label: "Assignments / Grading", to: "/admin/university/grading", icon: ClipboardList },
      { label: "Exams / Question Bank", to: "/admin/university/exams", icon: ClipboardList },
      { label: "Transcripts", to: "/admin/university/transcripts", icon: FileText },
      { label: "Subscriptions", to: "/admin/subscriptions", icon: DollarSign },
      { label: "CE Calendar", to: "/admin/university/ce", icon: Calendar },
      { label: "Clinical Residency", to: "/admin/university/residency", icon: Stethoscope },
      { label: "Alumni", to: "/admin/university/alumni", icon: Award },
      { label: "Board", to: "/admin/university/board", icon: Users },
      { label: "Billing", to: "/admin/university/billing", icon: DollarSign },
      { label: "Community", to: "/admin/university/community", icon: MessageSquare },
    ],
  },
  clinic: {
    label: "Clinic",
    icon: Stethoscope,
    items: [
      { label: "Clinic Hub", to: "/admin/clinic", icon: Stethoscope },
      { label: "Protocols", to: "/admin/clinic/protocols", icon: Pill },
      { label: "Clients (legacy)", to: "/admin", icon: Users },
      { label: "KCIS Reporting", to: "/admin/kcis-adaptive-reporting", icon: FileText },
    ],
  },
  coaching: {
    label: "Coaching",
    icon: Compass,
    items: [
      { label: "Coaching Hub", to: "/admin/coaching", icon: Compass },
      { label: "Clients", to: "/admin/coaching/clients", icon: Users },
      { label: "Programs", to: "/admin/coaching/programs", icon: Sparkles },
      { label: "Identity Submissions", to: "/admin/identity", icon: FileText },
    ],
  },
  "comfort-care": {
    label: "Comfort Care",
    icon: Heart,
    items: [
      { label: "Comfort Care Hub", to: "/admin/comfort-care", icon: Heart },
      { label: "Referrals", to: "/admin/comfort-care/referrals", icon: ClipboardList },
      { label: "Facilities", to: "/admin/comfort-care", icon: Building },
    ],
  },
  community: {
    label: "Community",
    icon: MessageSquare,
    items: [
      { label: "Moderation", to: "/admin/community", icon: MessageSquare },
      { label: "University Community", to: "/admin/university/community", icon: MessageSquare },
    ],
  },
};

export default function AdminShell({ door = "university", title, children, testid }) {
  const location = useLocation();
  const cfg = NAV[door] || NAV.university;
  const [open, setOpen] = useState(false);

  const items = useMemo(() => cfg.items, [cfg]);

  return (
    <div className="min-h-screen bg-[#FBF8F2] flex" data-testid={testid || `admin-shell-${door}`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-5 py-5 border-b border-slate-100">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-purple-900" data-testid="nav-back-hub">
            <ArrowLeft className="w-3 h-3" />Back to Admin Hub
          </Link>
          <div className="mt-3 inline-flex items-center gap-2">
            <cfg.icon className="w-5 h-5 text-purple-900" />
            <div className="font-display text-base text-slate-900">{cfg.label}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {items.map((it) => {
            const isActive = location.pathname === it.to || (it.to !== "/admin" && location.pathname.startsWith(it.to + "/"));
            return (
              <Link
                key={it.to + it.label}
                to={it.to}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                  isActive ? "bg-purple-50 text-purple-900 font-medium" : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`nav-${door}-${it.to.split("/").pop() || "root"}`}
              >
                <it.icon className={`w-4 h-4 ${isActive ? "text-purple-900" : "text-slate-400"}`} />
                <span className="flex-1">{it.label}</span>
                {isActive && <ChevronRight className="w-3 h-3" />}
              </Link>
            );
          })}
        </nav>
        <div className="px-5 py-3 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
          KISSED Clinic OS · {cfg.label}
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-white h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
              <div className="inline-flex items-center gap-2"><cfg.icon className="w-5 h-5 text-purple-900" /><div className="font-display">{cfg.label}</div></div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="nav-mobile-close"><X className="w-4 h-4" /></button>
            </div>
            <Link to="/admin/dashboard" onClick={() => setOpen(false)} className="px-5 py-3 text-xs text-slate-500 inline-flex items-center gap-1.5 border-b border-slate-100">
              <ArrowLeft className="w-3 h-3" />Back to Admin Hub
            </Link>
            <nav className="flex-1 overflow-y-auto py-3 px-2">
              {items.map((it) => {
                const isActive = location.pathname === it.to;
                return (
                  <Link key={it.to + it.label} to={it.to} onClick={() => setOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-purple-50 text-purple-900 font-medium" : "text-slate-700"}`}>
                    <it.icon className="w-4 h-4" />{it.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="nav-mobile-open"><Menu className="w-5 h-5" /></button>
            <div className="font-display text-base sm:text-lg text-slate-900" data-testid="admin-page-title">
              <span className="text-slate-400 text-xs uppercase tracking-wider mr-2 hidden sm:inline">{cfg.label}</span>
              {title}
            </div>
          </div>
          <Link to="/practitioner" className="text-xs text-purple-900 hover:underline">My Portal →</Link>
        </header>
        <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-7xl w-full mx-auto">
          <div className="mb-4"><SubscriptionBanner /></div>
          {children}
        </main>
      </div>
    </div>
  );
}
